import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageSquareHeart, Star, CheckCircle, Send, ThumbsUp, ThumbsDown, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RatingStars = ({ value, onChange, label }) => (
  <div>
    <label className="text-sm font-semibold text-gray-700 mb-2 block">{label}</label>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="p-1 transition-transform hover:scale-110"
          data-testid={`rating-star-${label.toLowerCase().replace(/\s+/g, '-')}-${star}`}
        >
          <Star
            size={28}
            className={star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        </button>
      ))}
    </div>
  </div>
);

const BetaFeedbackSurvey = () => {
  const [submitted, setSubmitted] = useState(false);
  const [previousFeedback, setPreviousFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    prepared_rating: 0,
    platform_rating: 0,
    most_valuable: '',
    student_impact: '',
    improvement: '',
    would_recommend: null,
    testimonial_consent: false,
  });

  useEffect(() => {
    fetchMyFeedback();
  }, []);

  const fetchMyFeedback = async () => {
    try {
      const res = await axios.get(`${API}/feedback/my`);
      setPreviousFeedback(res.data.feedback || []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.prepared_rating === 0 || form.platform_rating === 0) {
      toast.error('Please provide both ratings.');
      return;
    }
    if (!form.most_valuable.trim() || !form.student_impact.trim()) {
      toast.error('Please answer the two text questions.');
      return;
    }
    if (form.would_recommend === null) {
      toast.error('Please indicate if you would recommend MOSAIC.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, form);
      toast.success('Thank you for your feedback!');
      setSubmitted(true);
      fetchMyFeedback();
    } catch (err) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="beta-feedback-tab">
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <MessageSquareHeart size={20} className="text-[#002FA7]" />
          Beta Feedback
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Your feedback shapes MOSAIC. Tell us what's working and what could be better — it takes 2 minutes.
        </p>
      </div>

      {submitted ? (
        <div className="border-2 border-emerald-300 bg-emerald-50 p-8 text-center" data-testid="feedback-success">
          <CheckCircle size={48} className="text-emerald-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Feedback Received!
          </h3>
          <p className="text-gray-600 mb-4">
            Your input directly influences what we build next. Thank you for helping shape MOSAIC.
          </p>
          <Button
            onClick={() => { setSubmitted(false); setForm({ prepared_rating: 0, platform_rating: 0, most_valuable: '', student_impact: '', improvement: '', would_recommend: null, testimonial_consent: false }); }}
            variant="outline"
            className="btn-sharp"
            data-testid="feedback-submit-another"
          >
            Submit Another Response
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl" data-testid="feedback-form">
          {/* Ratings */}
          <div className="grid sm:grid-cols-2 gap-6">
            <RatingStars
              value={form.prepared_rating}
              onChange={(v) => setForm({ ...form, prepared_rating: v })}
              label="How prepared do your students feel for WIDA?"
            />
            <RatingStars
              value={form.platform_rating}
              onChange={(v) => setForm({ ...form, platform_rating: v })}
              label="Overall platform experience"
            />
          </div>

          {/* Text Questions — designed to elicit testimonial-ready quotes */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              What's most valuable about MOSAIC for your classroom? *
            </label>
            <Textarea
              placeholder="Example: The culturally responsive questions let my students see themselves in the material..."
              value={form.most_valuable}
              onChange={(e) => setForm({ ...form, most_valuable: e.target.value })}
              rows={3}
              className="border-2 rounded-none"
              data-testid="feedback-most-valuable"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              How has MOSAIC impacted your students so far? *
            </label>
            <Textarea
              placeholder="Example: My students are more confident with the WIDA format after practicing with the listening questions..."
              value={form.student_impact}
              onChange={(e) => setForm({ ...form, student_impact: e.target.value })}
              rows={3}
              className="border-2 rounded-none"
              data-testid="feedback-student-impact"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">
              What could we improve?
            </label>
            <Textarea
              placeholder="Any features you'd like added, things that are confusing, or changes you'd suggest..."
              value={form.improvement}
              onChange={(e) => setForm({ ...form, improvement: e.target.value })}
              rows={2}
              className="border-2 rounded-none"
              data-testid="feedback-improvement"
            />
          </div>

          {/* Would Recommend */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-3 block">
              Would you recommend MOSAIC to a colleague? *
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, would_recommend: true })}
                className={`flex items-center gap-2 px-6 py-3 border-2 transition-all ${
                  form.would_recommend === true
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-bold'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid="feedback-recommend-yes"
              >
                <ThumbsUp size={18} /> Yes
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, would_recommend: false })}
                className={`flex items-center gap-2 px-6 py-3 border-2 transition-all ${
                  form.would_recommend === false
                    ? 'border-red-400 bg-red-50 text-red-700 font-bold'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                data-testid="feedback-recommend-no"
              >
                <ThumbsDown size={18} /> Not yet
              </button>
            </div>
          </div>

          {/* Testimonial Consent */}
          <div className="border-2 border-blue-200 bg-blue-50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.testimonial_consent}
                onChange={(e) => setForm({ ...form, testimonial_consent: e.target.checked })}
                className="mt-1 accent-blue-600"
                data-testid="feedback-testimonial-consent"
              />
              <div>
                <div className="font-semibold text-sm text-blue-900">Share my words (optional)</div>
                <div className="text-xs text-blue-700">
                  I give permission for MOSAIC to use my feedback (with my name and school) as a testimonial
                  on the website or marketing materials. You can withdraw this at any time.
                </div>
              </div>
            </label>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="h-12 px-8 bg-[#002FA7] hover:bg-[#001845] text-white font-bold border-2 border-[#001845]"
            style={{ borderRadius: 0 }}
            data-testid="feedback-submit-btn"
          >
            {submitting ? (
              <><div className="loading-spinner w-4 h-4 mr-2 border-white border-t-transparent" /> Submitting...</>
            ) : (
              <><Send size={16} className="mr-2" /> Submit Feedback</>
            )}
          </Button>
        </form>
      )}

      {/* Previous Submissions */}
      {previousFeedback.length > 0 && (
        <div className="border-t-2 border-gray-200 pt-6 mt-8">
          <h4 className="text-sm font-bold text-gray-500 mb-3 flex items-center gap-2">
            <Clock size={14} /> Your Previous Feedback ({previousFeedback.length})
          </h4>
          <div className="space-y-3">
            {previousFeedback.map((fb, i) => (
              <div key={i} className="border border-gray-200 bg-gray-50 p-4 text-sm" data-testid={`previous-feedback-${i}`}>
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-xs text-gray-400">{new Date(fb.submitted_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={12} className={s <= fb.platform_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                    ))}
                  </span>
                  <span className={`text-xs px-2 py-0.5 font-bold ${fb.would_recommend ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                    {fb.would_recommend ? 'Would Recommend' : 'Not Yet'}
                  </span>
                </div>
                <p className="text-gray-700"><strong>Most valuable:</strong> {fb.most_valuable}</p>
                <p className="text-gray-700 mt-1"><strong>Student impact:</strong> {fb.student_impact}</p>
                {fb.improvement && <p className="text-gray-500 mt-1"><strong>Improvement:</strong> {fb.improvement}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BetaFeedbackSurvey;
